import { entries, filter, map, pipe, toArray } from '@fxts/core';
import { makeEmptyScreenNavigate } from '../app/navigate';
import { LocalsStoreFunding, LocalsStoreMembership } from '../type/locals';
import { isAllCrewByDomain } from '../util';
import { makePlusUrl } from '../util/url';
import { ValueOf } from '../../../../shared/type/util.type.ts';
import { ECreatorShopLnbMenu } from '@services/MarppleShop/shared/enum/ECreatorShopLnbMenu.ts';
import { ECateRootId } from '@services/MarppleShop/shared/enum/ECateRootId.ts';
import { ELang } from '@services/MarppleShop/shared/enum/ELang.ts';

export const MPS_ADDRESS = '서울 금천구 가산디지털1로 75-24 아이에스비즈타워 10층';
export const MPS_ADDRESS_EN = 'KR 08589 10F, 75-24, Gasan digital 1-ro, Geumcheon-gu, Seoul, Korea';
export const MPS_ADDRESS_ENEN = '3-8 Apple Vly, Irvine CA 92602';
export const MP_ADDRESS_JP = '〒08589 10F, 75-24, Gasan digital 1-ro, Geumcheon-gu, Seoul, Korea';
export const MP_MAIL_ORDER_SALES = '2021-서울금천-1710';

export const TERMS = {
  service: 'service',
  privacy: 'privacy',
  studio_service: 'studio_service',
  studio_privacy: 'studio_privacy',
};

export const MPS_ADDRESS_LANG = {
  kr: MPS_ADDRESS,
  en: MPS_ADDRESS_EN,
  jp: MP_ADDRESS_JP,
};

export const MPS_CUSTOMER_CENTER_NUMBER = {
  kr: '1566-5496',
  en: '1566-5496',
  jp: '+82) 050 5840 0207',
};

export type GNB_MENU = {
  url: string;
  name: string;
  key: string;
  post_message?: object;
  count?: number;
};

export type TCreatorShopLnbMenu = {
  url: string;
  name: string;
  key: ECreatorShopLnbMenu;
  is_selected: boolean;
  post_message?: object;
  count?: number;
};

export type gnb_menu_names =
  | 'creators'
  | 'best'
  | 'limited'
  | 'all_products'
  | 'creator_all_products'
  | 'contents'
  | 'review';

export function getGnbMenu(): Record<gnb_menu_names, GNB_MENU>;
export function getGnbMenu(gnb_menu_name: gnb_menu_names): GNB_MENU;
export function getGnbMenu(gnb_menu_name?: gnb_menu_names) {
  const menus = {
    creators: {
      url: '/@/creators',
      name: ET('mps2::search::creator'),
      key: 'creators',
    },
    best: {
      url: `/@/products?type=best`,
      name: ET('mps2::product::best'),
      key: 'best',
    },
    limited: {
      url: `/@/products?type=limited`,
      name: ET('mps2::product::limited'),
      key: 'limited',
    },
    all_products: {
      url: '/@/products',
      name: ET('mps2::product::all'),
      key: 'all_products',
    },
    creator_all_products: {
      url: '/products',
      name: ET('mps2::product::all'),
      key: 'creator_all_products',
    },
    contents: {
      url: '/@/todays',
      name: ET('콘텐츠'),
      key: 'contents',
    },
    review: {
      url: '/@/communities',
      name: ET('리뷰'),
      key: 'review',
    },
  };

  return gnb_menu_name ? menus[gnb_menu_name] : menus;
}

export const CREATOR_GNB_MENU_MAP = (
  {
    domain_name,
    crew_domain_name,
    cate_root_id,
    fan_letter_count,
    review_count,
  }: {
    domain_name: string;
    crew_domain_name?: string;
    cate_root_id?: ECateRootId;
    fan_letter_count?: number;
    review_count?: number;
  } = { domain_name: '' },
): Record<
  | 'all'
  | 'all_products'
  | typeof ECreatorShopLnbMenu.ShopHome
  | typeof ECreatorShopLnbMenu.Challenges
  | typeof ECreatorShopLnbMenu.Goods
  | typeof ECreatorShopLnbMenu.Handmade
  | typeof ECreatorShopLnbMenu.BrandDeal
  | typeof ECreatorShopLnbMenu.Ebook
  | typeof ECreatorShopLnbMenu.FanLetter
  | typeof ECreatorShopLnbMenu.Review,
  GNB_MENU
> => ({
  all: {
    url: `/${domain_name}/products?cate_root_id=${cate_root_id}`,
    name: ET('mps2::product::all'),
    key: 'all_products',
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/products?cate_root_id=${cate_root_id}`)
        : `/${domain_name}/products?cate_root_id=${cate_root_id}`,
    ),
  },
  all_products: {
    url: `/${domain_name}/products`,
    name: ET('mps2::search::product'),
    key: 'all_products',
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/products`)
        : `/${domain_name}/products`,
    ),
  },
  shop_home: {
    url: `/${domain_name}`,
    name: ET('creator_shop::lnb_creator_shop::shophome'),
    key: ECreatorShopLnbMenu.ShopHome,
  },
  challenges: {
    url: `/${domain_name}/fundings`,
    name: ET('preorder::lnb_pre_order_challenge'),
    key: ECreatorShopLnbMenu.Challenges,
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/challenges`)
        : `/${domain_name}/challenges`,
    ),
  },
  goods: {
    url: `/${domain_name}/products?cate_root_id=${ECateRootId.Goods}`,
    name: ET('creator_shop::lnb_creator_shop::goods'),
    key: ECreatorShopLnbMenu.Goods,
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/products?cate_root_id=${ECateRootId.Goods}`)
        : `/${domain_name}/products?cate_root_id=${ECateRootId.Goods}`,
    ),
  },
  handmade: {
    url: `/${domain_name}/products?cate_root_id=${ECateRootId.Handmade}`,
    name: ET('creator_shop::lnb_creator_shop::handmade'),
    key: ECreatorShopLnbMenu.Handmade,
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/products?cate_root_id=${ECateRootId.Handmade}`)
        : `/${domain_name}/products?cate_root_id=${ECateRootId.Handmade}`,
    ),
  },
  brand_deal: {
    url: `/${domain_name}/products?cate_root_id=${ECateRootId.BrandDeal}`,
    name: ET('creator_shop::lnb_creator_shop::branddeal'),
    key: ECreatorShopLnbMenu.BrandDeal,
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/products?cate_root_id=${ECateRootId.BrandDeal}`)
        : `/${domain_name}/products?cate_root_id=${ECateRootId.BrandDeal}`,
    ),
  },
  ebook: {
    url: `/${domain_name}/products?cate_root_id=${ECateRootId.Ebook}`,
    name: ET('creator_shop::lnb_creator_shop::ebook'),
    key: ECreatorShopLnbMenu.Ebook,
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/products?cate_root_id=${ECateRootId.Ebook}`)
        : `/${domain_name}/products?cate_root_id=${ECateRootId.Ebook}`,
    ),
  },
  fan_letter: {
    url: `/${domain_name}/fan-letter`,
    name: ET('creator_shop::lnb_creator_shop::fanletter'),
    key: ECreatorShopLnbMenu.FanLetter,
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/fan-letter`)
        : `/${domain_name}/fan-letter`,
    ),
    count: fan_letter_count,
  },
  review: {
    url: `/${domain_name}/review`,
    name: ET('creator_shop::lnb_creator_shop::review'),
    key: ECreatorShopLnbMenu.Review,
    post_message: makeEmptyScreenNavigate(
      crew_domain_name ? makePlusUrl(crew_domain_name, `/${domain_name}/review`) : `/${domain_name}/review`,
    ),
    count: review_count,
  },
});

export const CREATOR_ADDITIONAL_GNB_MENU_MAP = (
  { domain_name, crew_domain_name }: { domain_name: string; crew_domain_name?: string } = { domain_name: '' },
): Record<'membership' | 'commission' | 'support', GNB_MENU> => ({
  membership: {
    url: `/${domain_name}/membership/main`,
    name: ET('creator_shop::lnb_creator_shop::membership'),
    key: 'membership',
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/membership/main`)
        : `/${domain_name}/membership/main`,
    ),
  },
  commission: {
    url: `/${domain_name}/membership/commission`,
    name: ET('creator_shop::lnb_creator_shop::commission'),
    key: 'commission',
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/membership/commission`)
        : `/${domain_name}/membership/commission`,
    ),
  },
  support: {
    url: `/${domain_name}/membership/support`,
    name: ET('creator_shop::lnb_creator_shop::support'),
    key: 'support',
    post_message: makeEmptyScreenNavigate(
      crew_domain_name
        ? makePlusUrl(crew_domain_name, `/${domain_name}/membership/support`)
        : `/${domain_name}/membership/support`,
    ),
  },
});

export const create_membership_gnb_groups = (
  creator_additional_gnb_menu_list: ReturnType<typeof CREATOR_ADDITIONAL_GNB_MENU_MAP>,
  locals_store_membership?: LocalsStoreMembership,
) => {
  const membership_exist_gnb_map = {
    membership_plan_existed: 'membership',
    membership_commission_existed: 'commission',
    membership_support_existed: 'support',
  } as const;

  return pipe(
    membership_exist_gnb_map,
    entries,
    filter(([key]) => locals_store_membership?.[key]),
    map(([, value]) => creator_additional_gnb_menu_list[value]),
    toArray,
  );
};

export const GNB_MENU_GROUPS = {
  mps: (product_menus: GNB_MENU[]) => [getGnbMenu('all_products'), ...product_menus],
  plus: (product_menus: GNB_MENU[]) => [
    getGnbMenu('creators'),
    ...product_menus,
    getGnbMenu('contents'),
    getGnbMenu('review'),
  ],
  creator_shop: (option: {
    store_domain_name: string;
    plus_domain_name?: string;
    store_membership?: LocalsStoreMembership;
    store_funding?: LocalsStoreFunding;
    is_mobile?: boolean;
  }) => {
    if (isAllCrewByDomain(option.plus_domain_name)) {
      /** @desc All Crew 인 경우 링크에 domain_name 을 붙일 필요가 없음 */
      option.plus_domain_name = undefined;
    }

    const menu_map = CREATOR_GNB_MENU_MAP({
      domain_name: option.store_domain_name,
      crew_domain_name: option.plus_domain_name,
    });
    const additional_menu_map = CREATOR_ADDITIONAL_GNB_MENU_MAP({
      domain_name: option.store_domain_name,
      crew_domain_name: option.plus_domain_name,
    });

    return [
      ...(option.is_mobile ? [menu_map.shop_home] : []),

      /**
       * 펀딩이 존재한다면 펀딩 메뉴를 노출
       */
      ...(option.store_funding?.exist ? [menu_map.challenges] : []),

      menu_map.all_products,
      ...(!option.store_membership?.membership_existed
        ? []
        : [...create_membership_gnb_groups(additional_menu_map, option.store_membership)]),
      menu_map.fan_letter,
    ];
  },
  /**
   * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=22984-26819&t=z4cKW8HYJzr3CVgN-11
   */
  creator_shop_24: (option: {
    store_domain_name: string;
    plus_domain_name: string | undefined;
    store_membership:
      | {
          membership_existed: boolean;
          membership_plan_existed: boolean;
          membership_commission_existed: boolean;
          membership_support_existed: boolean;
        }
      | undefined;
    is_mobile: boolean;

    show_fundings: boolean;
    show_goods: boolean;
    show_handmade: boolean;
    show_brand_deal: boolean;
    show_ebook: boolean;

    fan_letter_count: number;
    review_count: number;
  }) => {
    if (isAllCrewByDomain(option.plus_domain_name)) {
      /** @desc All Crew 인 경우 링크에 domain_name 을 붙일 필요가 없음 */
      option.plus_domain_name = undefined;
    }

    const menu_map = CREATOR_GNB_MENU_MAP({
      domain_name: option.store_domain_name,
      crew_domain_name: option.plus_domain_name,
      fan_letter_count: option.fan_letter_count,
      review_count: option.review_count,
    });
    const additional_menu_map = CREATOR_ADDITIONAL_GNB_MENU_MAP({
      domain_name: option.store_domain_name,
      crew_domain_name: option.plus_domain_name,
    });

    /**
     * @see ECreatorShopLnbMenu
     */
    return [
      menu_map.shop_home,
      ...(ET.lang === ELang.Kr && option.show_fundings ? [menu_map.challenges] : []),
      ...(option.show_goods ? [menu_map.goods] : []),
      ...(ET.lang === ELang.Kr && option.show_handmade ? [menu_map.handmade] : []),
      ...(ET.lang === ELang.Kr && option.show_brand_deal ? [menu_map.brand_deal] : []),
      ...(ET.lang === ELang.Kr && option.show_ebook ? [menu_map.ebook] : []),
      ...(ET.lang === ELang.Kr &&
      option.store_membership?.membership_existed &&
      option.store_membership.membership_plan_existed
        ? [additional_menu_map.membership]
        : []),
      ...(ET.lang === ELang.Kr &&
      option.store_membership?.membership_existed &&
      option.store_membership.membership_commission_existed
        ? [additional_menu_map.commission]
        : []),
      ...(ET.lang === ELang.Kr &&
      option.store_membership?.membership_existed &&
      option.store_membership.membership_support_existed
        ? [additional_menu_map.support]
        : []),
      menu_map.fan_letter,
      ...(option.review_count > 0 ? [menu_map.review] : []),
    ];
  },
};

export const processSelectCreatorShopLnbMenu = (
  menus: GNB_MENU[],
  creator_shop_lnb_menu_to_select: ECreatorShopLnbMenu,
) =>
  menus.map((menu) => ({
    ...menu,
    key: menu.key as ECreatorShopLnbMenu,
    is_selected: menu.key === creator_shop_lnb_menu_to_select || menus.length === 1,
  }));

export const MONEY_SIGN = {
  kr: '원',
  en: '$',
  jp: '¥',
} as const;
export type MONEY_SIGN = ValueOf<typeof MONEY_SIGN>;

export const LANGUAGES = ['kr', 'en', 'jp'];

export const INTL = { kr: '', en: '', jp: '' } as const;
export const INTL_NAME = { kr: '국문', en: '영문', jp: '일문' } as const;

export const EXCLUDE_MAKER_MODULE = 'exclude_maker_module';

export const PARTNER_PLUS_ID = 8;

export const isPartnerCrew = (crew_id: number) => {
  return crew_id === PARTNER_PLUS_ID;
};
